import { Fragment, useMemo, useState } from "react";

import type { Trade, WalletTier } from "../api/types";
import { useNow } from "../hooks/useNow";
import { useTapeStats } from "../hooks/useTapeStats";
import { accountLabel } from "../lib/accounts";
import { formatTime, shortId } from "../lib/format";
import * as money from "../lib/money";
import { bySize, hiddenBySizeNote, looksDoubleCounted, sizeThreshold, tapeRows } from "../lib/tape";
import { dominantTier } from "../lib/tier";
import { isMaker, takerAddress, tradeKey } from "../lib/trades";
import { useAccounts } from "../store/useAccounts";
import { useTape } from "../store/useTape";
import { useTiers } from "../store/useTiers";
import { Empty, Panel } from "./Panel";
import { Counterparty } from "./Tier";

interface Props {
  /** null — общая лента по всем рынкам. */
  conditionId: string | null;
  limit?: number | undefined;
}

/**
 * Лента сделок.
 *
 * Лента рынка выводится фильтром из общего стора, а не отдельным запросом:
 * web_api отдаёт при подключении историю ОБЩЕЙ ленты независимо от подписок,
 * так что второй источник тут неоткуда взять и незачем.
 */
export function TapePanel({ conditionId, limit = 150 }: Props) {
  const trades = useTape((s) => s.trades);
  const clear = useTape((s) => s.clear);
  // Тир берётся ПО АДРЕСУ из отдельного стора, а не из строки сделки: незнакомый
  // кошелёк приезжает без квалификации и получает её секундами позже сообщением
  // profile_update, дорисовывая уже нарисованную строку (см. useTiers).
  const tiers = useTiers((s) => s.tiers);

  /**
   * Отсечка мелочи, введённая оператором. Своя у каждой панели и не переживает
   * перезагрузку: это ВЗГЛЯД на ленту, а не настройка торговли, и сохранённый
   * порог означал бы ленту, молча укороченную вчерашним решением.
   */
  const [minRaw, setMinRaw] = useState("");
  const min = sizeThreshold(minRaw);

  const market = useMemo(
    () => (conditionId ? trades.filter((t) => t.condition_id === conditionId) : trades),
    [trades, conditionId],
  );
  // Отсечка ДО обрезки по limit: отфильтруй после — и полторы сотни строк
  // окажутся сотней мелких, из которых видно три крупных, то есть фильтр съест
  // сам себя на активном рынке.
  const kept = useMemo(() => bySize(market, min), [market, min]);
  const rows = useMemo(() => kept.slice(0, limit), [kept, limit]);
  const hidden = hiddenBySizeNote(market.length - kept.length, min);

  return (
    <Panel
      title="Лента сделок"
      subtitle={conditionId ? "этот рынок" : "все рынки"}
      // Плотная карточка: у панели с прокручиваемой таблицей снятые с рамки
      // пиксели превращаются в строки таблицы, а не в пустоту.
      dense
      actions={
        <div className="flex items-center gap-3">
          {/* Порог — В ШАПКЕ и всегда, а не под кнопкой «фильтры»: он меняет то,
              что видно в панели, и обязан быть виден рядом с тем, что показывает.
              Единица подписана: доллар и share тут путать нельзя. */}
          <label className="flex items-center gap-1 text-xs text-[var(--color-muted)]">
            от
            <input
              value={minRaw}
              onChange={(e) => setMinRaw(e.target.value)}
              inputMode="decimal"
              placeholder="0"
              title="показывать сделки не мельче этого размера, в share. Пусто или 0 — показывать все"
              className={
                "num w-16 rounded border border-[var(--color-border)] bg-transparent px-1.5 py-0.5 " +
                "text-right text-xs text-white outline-none placeholder:text-[var(--color-muted)] " +
                "focus:border-[var(--color-accent)]"
              }
            />
            share
          </label>
          {trades.length > 0 && (
            <button onClick={clear} className="text-xs text-[var(--color-muted)] hover:text-white">
              очистить
            </button>
          )}
        </div>
      }
    >
      {/* Счётчик — в теле панели, а не в её шапке. В шапке он делил ширину с
          заголовком и в колонке рынка рвался посреди числа: «$642.» на одной
          строке, «1» на другой. Здесь ему доступна вся ширина панели, а
          величины стоят выровненными колонками — только так и сравнивают наши
          share с чужими. */}
      {conditionId && <VolumeCounter conditionId={conditionId} />}

      {rows.length === 0 ? (
        <Empty>
          {/* «Отсечено всё» и «сделок не было» — разные факты, и одинаковая
              пустота на их месте отвечала бы «рынок стоит» там, где он идёт.
              Числом отвечает подпись ниже, она видна и при пустой таблице. */}
          {hidden
            ? "Все сделки мельче порога."
            : conditionId
              ? "По этому рынку сделок пока не было."
              : "Сделок пока нет — лента наполнится, когда трейдер начнёт получать данные."}
        </Empty>
      ) : (
        /* Потолок таблицы — по ФАКТИЧЕСКОМУ месту под липкой полосой, а не по
           высоте экрана: `vh` меряет экран целиком, а таблица живёт в остатке
           под полосой, и разница между ними давала двойную прокрутку — сначала
           страницы, потом внутри таблицы. Переменную ставит страница рынка
           (pages/MarketPage.tsx).

           Пол в 16rem обязателен: полоса растёт при открытом редакторе пресетов,
           плашке закончившегося окна и замечаниях, и без пола таблица
           схлопывалась бы ровно тогда, когда на экране тревога.

           Только с xl: ниже полоса не липкая, и прежний `vh` там честен. */
        <div className="max-h-[70vh] overflow-y-auto xl:max-h-[max(16rem,calc(100dvh-var(--pm-stack-top,0px)-11rem))]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--color-panel)]">
              <tr className="text-left text-[var(--color-muted)]">
                <th className="pb-2 font-normal">Время</th>
                {!conditionId && <th className="pb-2 font-normal">Рынок</th>}
                {/* Аккаунт есть только у НАШИХ сделок: у публичного принта
                    рынка нашего участника нет вовсе. */}
                <th className="pb-2 font-normal">Аккаунт</th>
                <th className="pb-2 font-normal">Сторона</th>
                <th className="pb-2 text-right font-normal">Цена</th>
                <th className="pb-2 text-right font-normal">Размер</th>
                <th className="pb-2 text-right font-normal">Нотионал</th>
                {/* Колонка пуста у публичных принтов рынка, и это не дефект:
                    событие last_trade_price не несёт ни одного адреса, поэтому
                    контрагент известен ТОЛЬКО у наших сделок. */}
                <th className="pb-2 font-normal">Контрагент</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t, i) => (
                <TapeRow
                  key={tradeKey(t)}
                  trade={t}
                  showMarket={!conditionId}
                  fresh={i === 0}
                  tiers={tiers}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hidden && (
        // Отсечка называется вслух и числом — как в панели участников: молча
        // укоротившаяся лента читается как затишье на рынке, которого нет.
        <p className="mt-2 text-xs text-[var(--color-muted)]">{hidden}</p>
      )}
    </Panel>
  );
}

/**
 * Счётчик объёма: сколько увидели МЫ и сколько насчитал Polymarket.
 *
 * Заведён по замечанию оператора: он насчитал по ленте порядка пяти тысяч там, где
 * интерфейс биржи показывал «$16.5K Vol.». Числа стоят РЯДОМ, потому что смысл
 * имеет их разница, а не каждое по себе, — и рядом же стоит возраст чужого числа,
 * без которого разница читается как потеря сделок, которой нет.
 *
 * Считается на сервере, а не по стору ленты: тот держит тысячу последних сделок на
 * всю вкладку и за активное окно вытесняется несколько раз (см. useTapeStats).
 *
 * Вся логика — в lib/tape.ts, здесь разметка.
 */
function VolumeCounter({ conditionId }: { conditionId: string }) {
  const q = useTapeStats(conditionId);
  // Часы тикают в листе дерева — правило hooks/useNow.ts. Возраст чужого числа
  // обязан расти сам, иначе минутная давность выглядит секундной до следующего
  // ответа.
  const nowMs = useNow(5000);
  const t = q.data;

  if (!t) return null;

  const rows = tapeRows(t, nowMs);

  return (
    <div className="mb-3 border-b border-[var(--color-border)] pb-2 text-xs">
      {!t.known && (
        // Словами, а не пустым местом: пустота на месте счётчика читается как
        // «сделок не было», а это другой факт.
        <p className="text-amber-300">{t.reason || "счётчик недоступен"}</p>
      )}

      {rows.length > 0 && (
        // Колонки, а не строка через разделители: величины сравнивают глазом по
        // вертикали — наши share против чужих, — и выравнивание здесь суть, а не
        // оформление. Единицы подписаны у каждого числа: доллар и share путать
        // нельзя, «$… Vol.» биржи — это штуки контрактов, а не деньги.
        <div
          className={`grid grid-cols-[auto_auto_auto_1fr] items-baseline gap-x-3 gap-y-0.5${
            t.stale ? " opacity-50" : ""
          }`}
          title={
            "наша лента — то, что видел трейдер; Polymarket — объём по версии биржи, " +
            "в ШТУКАХ контрактов: «$… Vol.» в её интерфейсе это Σ size, а не деньги"
          }
        >
          {rows.map((r) => (
            <Fragment key={r.label}>
              <span className="text-[var(--color-muted)]">{r.label}</span>
              <span className="num text-right">
                {r.trades && (
                  <>
                    {r.trades} <span className="text-[var(--color-muted)]">сд.</span>
                  </>
                )}
              </span>
              <span className="num text-right">
                {r.shares} <span className="text-[var(--color-muted)]">share</span>
              </span>
              {/* Деньги и приписка делят последнюю ячейку: у нашей строки это
                  сумма и момент начала счёта, у строки биржи — только возраст
                  числа. Возраст ОБЯЗАН быть виден: без него разница с нашим
                  счётом читается как потеря сделок, которой нет. */}
              <span className="num text-right text-[var(--color-muted)]">
                {r.money}
                {r.money && r.note ? " · " : ""}
                {r.note}
              </span>
            </Fragment>
          ))}
        </div>
      )}

      {looksDoubleCounted(t) && (
        <p
          className="mt-1 text-amber-300"
          title={
            "половины разбивки по двум исходам почти равны: похоже, один матч " +
            "виден как два принта. Проверяется сверкой: make gate-tape CONDITION=…"
          }
        >
          ×2? половины по исходам почти равны
        </p>
      )}
    </div>
  );
}

function TapeRow({
  trade,
  showMarket,
  fresh,
  tiers,
}: {
  trade: Trade;
  showMarket: boolean;
  fresh: boolean;
  tiers: Readonly<Record<string, WalletTier>>;
}) {
  const color = trade.side === "BUY" ? "var(--color-buy)" : "var(--color-sell)";
  // Контрагентов у сделки бывает несколько (наш ордер закрыли три чужих), а в
  // строку помещается один значок — показываем самого заметного, а остальных
  // считаем счётчиком: скрыть их молча значило бы выдать сделку против трёх
  // кошельков за сделку против одного.
  const top = dominantTier(trade.counterparties, tiers);
  const total = trade.counterparties?.length ?? 0;
  // Строкой, а не всем составом: опрос состава иначе перерисовывал бы ленту.
  const account = useAccounts((s) =>
    trade.ours && trade.account ? accountLabel(s.data, trade.account) : "",
  );

  return (
    <tr className={`border-t border-[var(--color-border)] ${fresh ? "row-in" : ""}`}>
      <td className="num py-1 pr-3 text-[var(--color-muted)]">{formatTime(trade.timestamp)}</td>
      {showMarket && (
        <td className="num py-1 pr-3 text-[var(--color-muted)]">{shortId(trade.condition_id)}</td>
      )}
      <td className="py-1 pr-3 text-[var(--color-muted)]">{account}</td>
      <td className="py-1 pr-3" style={{ color }}>
        {trade.side === "BUY" ? "покупка" : "продажа"}
      </td>
      <td className="num py-1 pr-3 text-right" style={{ color }}>
        {trade.price}
      </td>
      <td className="num py-1 pr-3 text-right">{trade.size}</td>
      <td className="num py-1 pr-3 text-right text-[var(--color-muted)]">{notional(trade)}</td>
      <td className="py-1">{counterparty(trade, top, total, tiers)}</td>
    </tr>
  );
}

/**
 * Ячейка контрагента.
 *
 * Четыре разных состояния, и они НЕ должны выглядеть одинаково:
 *
 *   - публичный принт: контрагента нет физически, событие last_trade_price не
 *     несёт ни одного адреса;
 *   - наша ТЕЙКЕРСКАЯ сделка: контрагенты есть и известны — это те мейкеры,
 *     которых мы сняли;
 *   - наша МЕЙКЕРСКАЯ сделка: контрагент есть, но его адреса нет на проводе.
 *     Встречные строки в ней принадлежат соседям по сносу — мейкерам, которых
 *     закрыл тот же чужой тейкер-ордер, — и показать их как контрагентов
 *     значило бы отправить оператора смотреть профиль того, с кем он не
 *     торговал (ADR-0007 §1). Сам агрессор теперь приезжает из цепочки —
 *     позже строки и отдельной публикацией той же сделки;
 *   - незнакомый кошелёк: адрес есть, квалификации ещё нет, она дорисуется.
 *
 * Свести их к пустой ячейке значило бы выдать «мы не знаем» за «этого не было».
 */
function counterparty(
  trade: Trade,
  top: { address: string; tier: WalletTier } | undefined,
  total: number,
  tiers: Readonly<Record<string, WalletTier>>,
): React.ReactNode {
  if (trade.ours && isMaker(trade)) {
    // Агрессор, восстановленный из квитанции транзакции (ADR-0008). В ленте он
    // появляется на секунды позже самой строки — отдельной публикацией той же
    // сделки, которая замещает запись по id.
    const taker = takerAddress(trade);
    if (taker) {
      return (
        <Counterparty
          address={taker}
          tier={tiers[taker]}
          title="снял нашу заявку; адрес восстановлен из квитанции транзакции"
        />
      );
    }
    return (
      <span
        className="text-[var(--color-muted)]"
        title="Мы стояли в стакане, и нас снял агрессор: на проводе видны только мейкеры, адреса тейкера там нет вовсе. Он восстанавливается из квитанции транзакции — появится через несколько секунд."
      >
        тейкер скрыт
      </span>
    );
  }
  if (total === 0) {
    return <span className="text-[var(--color-muted)]">—</span>;
  }
  const address = top?.address ?? trade.counterparties?.[0] ?? "";
  return <Counterparty address={address} tier={top?.tier} extra={total - 1} />;
}

/** Нотионал с провода, а при его отсутствии — посчитанный точной арифметикой. */
function notional(t: Trade): string {
  if (t.notional) return t.notional;
  const price = money.parse(t.price);
  const size = money.parse(t.size);
  if (price === null || size === null) return "—";
  const v = money.mul(price, size);
  return v === null ? "—" : money.format(v);
}
